// Setup › Price book, Blueprint (DEC-019, FLO-31): the approved page, ui-web's PricebookView, drawn from the
// company's book. Every item a page at a time (pricebook.listItems), the categories as the tree
// (pricebook.listCategories), and for who approves them the new pictures waiting (pricebook.art.toApprove),
// which are the queue: Approve and next, Turn down with what the new one should show, Approve all shown.
// An item opens beside the list (pricebook.getItem): its pictures (PricebookArtPanel and its photos,
// pricebook.setMedia), its fields, what it uses, and its history with Put back (PricebookHistory); Save
// changes is pricebook.updateItem with the why; Add an item is pricebook.createItem; New category is
// pricebook.createCategory; Edit them together is one update per ticked item. The server decides who
// may; a refusal shows its words in the sheet. An imported item reads only while the company mirrors.
// Under the list: the page's other sections (option templates, the rules and pictures' switches), the
// spreadsheet and In stock, as before.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button, Link, TextField } from '@flowology/ui-web/fl';
import { ItemMedia, ItemUses } from '@flowology/ui-web';
import { FormSheet } from '@flowology/ui-web/patterns';
import {
  PricebookView,
  QueueRecord,
  type BookQueue,
  type PricebookBook,
  type SheetItem,
} from '@flowology/ui-web/pricebook';
import type { PictureFirst } from '@flowology/domain';
import { client } from '../api.ts';
import { refusal, unanswered } from '../history/customer-edit-model.ts';
import { MIRROR_LINE } from '../shared/mirror.ts';
import { CATALOG_LINK, type Category } from '../inventory/catalog-model.ts';
import type { PageCrumbStep } from '../Nav.tsx';
import { PricebookArtPanel } from './PricebookArt.tsx';
import { PricebookHistory } from './PricebookHistory.tsx';
import { PricebookSpreadsheet } from './PricebookSpreadsheet.tsx';
import {
  approvedWords,
  itemsWords,
  PICTURES_CHANGED,
  turnedDownWords,
  waitingLine,
  type Approved,
  type ToApprove,
} from './pictures-model.ts';
import {
  bookCategories,
  bookItemOf,
  applyBulk,
  bulkFields,
  bulkPlan,
  bulkPreview,
  COLUMNS,
  filtersFor,
  KIND_OPTIONS,
  newInput,
  newSheet,
  placeOptions,
  saveInput,
  sheetOfItem,
  TAX_OPTIONS,
  viewerOf,
  type BulkPlan,
} from './pricebook-book-model.ts';
import {
  hasDrawing,
  importedWords,
  itemEditable,
  photosFirst,
  showFirstInput,
  type Item,
} from './setup-model.ts';
import { useShownTheme } from '../blueprint/theme.ts';

const SHOWN = 60;
const READS_ONLY = 'Only the owner or a manager changes these.';

/** Every item, a page at a time: the server gives 200 at most, and a company's book runs past it. */
async function readBook(): Promise<{ items: Item[]; first: PictureFirst }> {
  const got: Item[] = [];
  let first: PictureFirst = 'drawing';
  let after: { name: string; id: string } | null = null;
  do {
    const r: { items: Item[]; next: { name: string; id: string } | null; showFirst?: PictureFirst } =
      await client.call('pricebook.listItems', {
        includeInactive: true,
        includeNotForSale: true,
        limit: 200,
        ...(after ? { after } : {}),
      });
    got.push(...r.items);
    if (r.showFirst) first = r.showFirst;
    after = r.next;
  } while (after);
  return { items: got, first };
}

export function PricebookPlace({
  held,
  mirroring,
  toCatalog,
  timeZone,
  crumb,
  children,
}: {
  held: readonly string[];
  mirroring: boolean;
  /** Who keeps the stock goes from here to the same parts in Stock's catalog. */
  toCatalog: boolean;
  timeZone: string;
  /** Setup, over the title (the frame's crumb). */
  crumb?: PageCrumbStep[];
  /** The page's other sections, under the list, before the spreadsheet. */
  children?: ReactNode;
}) {
  const canEdit = held.includes('pricebook.edit');
  const viewer = viewerOf(held);
  const [book, setBook] = useState<{ items: Item[]; first: PictureFirst } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [cats, setCats] = useState<Category[]>([]);
  const [waiting, setWaiting] = useState<{ pictures: ToApprove[]; total: number } | null>(null);
  const [pictureVersion, setPictureVersion] = useState(0);
  // an item as read on its own (its pictures, what it uses), by id; read again after a change
  const [details, setDetails] = useState<Record<string, Item>>({});
  const [said, setSaid] = useState<string | null>(null);
  const [imported, setImported] = useState<string | null>(null);
  const [addingCategory, setAddingCategory] = useState(false);

  useEffect(() => {
    let current = true;
    setError(null);
    readBook()
      .then((b) => current && setBook(b))
      .catch((e: unknown) => current && setError(refusal(e)));
    return () => {
      current = false;
    };
  }, [version]);
  useEffect(() => {
    let current = true;
    client
      .call<{ categories: Category[] }>('pricebook.listCategories', {})
      .then((r) => current && setCats(r.categories))
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [version]);
  useEffect(() => {
    if (!canEdit) return;
    let current = true;
    client
      .call<{ pictures: ToApprove[]; total: number }>('pricebook.art.toApprove', { limit: SHOWN })
      .then((r) => current && setWaiting(r))
      .catch(() => current && setWaiting(null));
    return () => {
      current = false;
    };
  }, [canEdit, version, pictureVersion]);

  const read = (id: string) => {
    client
      .call<Item>('pricebook.getItem', { id })
      .then((i) => setDetails((d) => ({ ...d, [id]: i })))
      .catch(() => undefined);
  };
  const reload = () => {
    setDetails({});
    setVersion((v) => v + 1);
  };
  const picturesChanged = () => {
    setPictureVersion((v) => v + 1);
    setVersion((v) => v + 1);
    window.dispatchEvent(new Event(PICTURES_CHANGED));
  };

  const items = book?.items ?? [];
  const first = book?.first ?? 'drawing';
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const itemOf = (id: string) => details[id] ?? byId.get(id) ?? null;
  // Edit them together over the ticked items: who can't change one here (imported) is skipped; a retry
  // of the same changes sends what didn't go in as first built (bulkPlan's kept)
  const retryOf = useRef<{ changes: string; kept: Map<string, BulkPlan['changes'][number]> } | null>(null);
  const planOf = (ids: string[], changes: Record<string, string>) =>
    bulkPlan(
      ids.map(itemOf).filter((i): i is Item => i !== null),
      changes,
      (i) => itemEditable(i, canEdit, mirroring),
      retryOf.current?.changes === JSON.stringify(changes) ? retryOf.current.kept : undefined,
    );
  const waitingIds = useMemo(
    () => new Set((waiting?.pictures ?? []).flatMap((p) => p.items.map((i) => i.id))),
    [waiting],
  );

  const approve = async (artIds: string[]) => {
    try {
      setSaid(approvedWords(await client.call<Approved>('pricebook.art.approve', { artIds })));
      picturesChanged();
    } catch (e) {
      setSaid(refusal(e));
    }
  };
  const turnDown = async (artId: string, request: string) => {
    try {
      const r = await client.call<{ rejected: boolean; jobId: string | null }>('pricebook.art.reject', {
        artId,
        ...(request.trim() ? { request: request.trim() } : {}),
      });
      setSaid(turnedDownWords(r));
      picturesChanged();
    } catch (e) {
      setSaid(refusal(e));
    }
  };

  const pictures = waiting?.pictures ?? [];
  const queue: PricebookBook['queue'] =
    canEdit && pictures.length
      ? (): BookQueue => ({
          filter: 'toapprove',
          note: `${pictures.length === 1 ? '1 new picture waits' : `${pictures.length} new pictures wait`} for an OK. Customers and technicians don’t see one until it’s approved.`,
          openLabel: `Review ${pictures.length === 1 ? 'it' : `${pictures.length} pictures`}`,
          title: 'Pictures to approve',
          lede: 'The new pictures, one at a time: approve one, or turn it down and say what the next should show.',
          label: 'Pictures to approve',
          total: pictures.length,
          summary: said ?? waitingLine(waiting?.total ?? pictures.length, pictures.length),
          doneText: 'That’s all of them. The list shows what’s left.',
          record: (n) => {
            const p = pictures[n];
            return p ? <PictureRecord key={p.artId} p={p} onTurnDown={(r) => turnDown(p.artId, r)} /> : null;
          },
          itemAt: (n) => pictures[n]?.items[0]?.id,
          primaryLabel: () => 'Approve and next',
          onPrimary: async (n) => {
            const p = pictures[n];
            if (!p) return false;
            await approve([p.artId]);
            return true;
          },
          ...(pictures.length > 1
            ? {
                approveAll: {
                  label: `Approve all ${pictures.length} shown`,
                  run: () => approve(pictures.map((p) => p.artId)),
                },
              }
            : {}),
          othersWaiting: 0,
        })
      : undefined;

  const view: PricebookBook = {
    items: items.map((i) => bookItemOf(i, waitingIds, first)),
    categories: bookCategories(cats),
    status: book ? 'ready' : error ? 'failed' : 'loading',
    failedText: error ?? undefined,
    onRetry: reload,
    filters: filtersFor(canEdit),
    columns: COLUMNS,
    queue,
    canAdd: canEdit,
    onSpreadsheet: () =>
      document.getElementById('pb-sheet-title')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    ...(canEdit && !mirroring ? { onNewCategory: () => setAddingCategory(true) } : {}),
    sheet: {
      of: (id) => {
        const i = itemOf(id);
        return i ? sheetOfItem(i, cats, first) : null;
      },
      fresh: () => newSheet(viewer === 'owner'),
      onOpen: read,
      pricesBy: 'the owner or a manager',
      readOnly: (id) => {
        const i = itemOf(id);
        if (!canEdit) return READS_ONLY;
        return i && !itemEditable(i, canEdit, mirroring) ? MIRROR_LINE : null;
      },
      save: async (id, s: SheetItem) => {
        if (id === 'new') {
          const built = newInput(s);
          if ('error' in built) throw new Error(built.error);
          try {
            await client.call('pricebook.createItem', built.input);
          } catch (e) {
            throw new Error(refusal(e), { cause: e });
          }
          reload();
          return;
        }
        const i = itemOf(id);
        if (!i) throw new Error('This item isn’t in the book any more. Close it and look again.');
        const built = saveInput(i, s);
        if ('error' in built) throw new Error(built.error);
        try {
          await client.call('pricebook.updateItem', built.input);
        } catch (e) {
          throw new Error(refusal(e), { cause: e });
        }
        reload();
      },
      props: (id) =>
        id === 'new'
          ? {
              places: placeOptions(cats),
              taxClasses: TAX_OPTIONS,
              kinds: KIND_OPTIONS,
              slots: {
                picture: <p className="fl-pbsheet__muted">Add its pictures once it’s in the book.</p>,
              },
            }
          : {
              places: placeOptions(cats),
              taxClasses: TAX_OPTIONS,
              slots: itemSlots(itemOf(id), {
                canChange: !!itemOf(id) && itemEditable(itemOf(id)!, canEdit, mirroring),
                first,
                timeZone,
                onPictures: () => {
                  read(id);
                  setVersion((v) => v + 1);
                },
                onPutBack: () => {
                  read(id);
                  setVersion((v) => v + 1);
                },
              }),
            },
    },
    ...(canEdit
      ? {
          bulk: {
            fields: bulkFields(cats),
            // mirrored items are skipped and said, never sent; each refusal is named with its reason
            preview: (ids, changes) => bulkPreview(planOf(ids, changes), cats),
            apply: async (ids, changes) => {
              const r = await applyBulk(
                planOf(ids, changes),
                (input) => client.call('pricebook.updateItem', input),
                refusal,
                unanswered,
              );
              retryOf.current = {
                changes: JSON.stringify(changes),
                kept: new Map(r.kept.map((c) => [c.item.id, c])),
              };
              reload();
              return { said: r.said, retry: r.retry };
            },
          },
        }
      : {}),
    // the app's own sections under the list keep Setup's Blueprint look (blueprint/setup.css); the page
    // above them is ui-web's, out of .bp-setup's reach
    below: (
      <div className="bp-setup flw">
        <div className="settings">
          {said ? (
            <p className="notice ok" role="status">
              {said}
            </p>
          ) : null}
          {children}
          <section className="sheet setup-card" aria-labelledby="pb-sheet-title">
            <h2 id="pb-sheet-title">As a spreadsheet</h2>
            <PricebookSpreadsheet
              canEdit={canEdit && !mirroring}
              onImported={(words) => {
                setImported(words);
                reload();
              }}
            />
            {imported ? (
              <p className="notice ok" role="status">
                {imported}
              </p>
            ) : null}
          </section>
          {toCatalog ? (
            // the catalog is these same items, not a copy: their units, vendor and bin are Stock's
            <section className="sheet setup-card" aria-labelledby="pb-stock-title">
              <h2 id="pb-stock-title">In stock</h2>
              <p className="small">
                The parts here are the ones Stock keeps. What the vendor sells each in, its vendor, bin and
                levels are in <a href={CATALOG_LINK}>Catalog and prices</a>.
              </p>
            </section>
          ) : null}
        </div>
      </div>
    ),
  };

  return (
    <>
      <PricebookView viewer={viewer} book={view} crumb={crumb} />
      {addingCategory ? (
        <NewCategory
          onClose={() => setAddingCategory(false)}
          onSaved={() => {
            setAddingCategory(false);
            setVersion((v) => v + 1);
          }}
        />
      ) : null}
    </>
  );
}

/** An item's own sections as the app draws them: its pictures, what it uses, its history. */
function itemSlots(
  item: Item | null,
  o: {
    canChange: boolean;
    first: PictureFirst;
    timeZone: string;
    onPictures: () => void;
    onPutBack: () => void;
  },
) {
  if (!item) return {};
  const uses = item.materials ?? [];
  return {
    picture: <ItemPictures item={item} canChange={o.canChange} first={o.first} onChanged={o.onPictures} />,
    ...(uses.length || item.details
      ? {
          madeOf: (
            <div className="flw item-uses">
              <ItemUses
                data={{ uses, details: item.details ?? null }}
                empty={item.type === 'service' ? undefined : null}
              />
            </div>
          ),
        }
      : {}),
    // the history once the item's own read is in (its row version is the one a put-back goes against)
    ...(item.media !== undefined
      ? {
          history: (
            <PricebookHistory
              item={item}
              timeZone={o.timeZone}
              canPutBack={o.canChange}
              onPutBack={o.onPutBack}
            />
          ),
        }
      : {}),
  };
}

/**
 * An item's pictures: its drawing (PricebookArtPanel, with its own Use, Make another and the rest), and
 * its photos above or under it, by which the company shows first; who may change it picks the photo that
 * leads them (pricebook.setMedia).
 */
function ItemPictures({
  item,
  canChange,
  first,
  onChanged,
}: {
  item: Item;
  canChange: boolean;
  first: PictureFirst;
  onChanged: () => void;
}) {
  const theme = useShownTheme();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const media = item.media ?? [];
  const lead = photosFirst(item, theme, first);
  const panel = <PricebookArtPanel itemId={item.id} name={item.name} />;
  const showFirst = async (fileId: string) => {
    const built = showFirstInput(item, fileId);
    if ('error' in built) return setError(built.error);
    setBusy(true);
    setError(null);
    try {
      await client.call('pricebook.setMedia', built.input);
      onChanged();
    } catch (e) {
      setError(refusal(e));
    }
    setBusy(false);
  };
  const photos =
    item.media !== undefined && (media.length > 0 || !hasDrawing(item)) ? (
      <>
        <ItemMedia
          data={{ name: item.name, media }}
          from={importedWords(item) ?? undefined}
          onFirst={canChange ? (m) => void showFirst(m) : undefined}
          busy={busy}
        />
        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}
      </>
    ) : null;
  return (
    <div>
      {lead ? (
        <>
          {photos}
          {panel}
        </>
      ) : (
        <>
          {panel}
          {photos}
        </>
      )}
    </div>
  );
}

/** A new picture in the queue: light and dark, the items it's for, what it shows; Turn down asks what next. */
function PictureRecord({ p, onTurnDown }: { p: ToApprove; onTurnDown: (request: string) => Promise<void> }) {
  const [asking, setAsking] = useState(false);
  const [request, setRequest] = useState('');
  const [busy, setBusy] = useState(false);
  const url = (s: { url: string | null; thumbUrl: string | null } | null) => s?.url ?? s?.thumbUrl ?? null;
  const light = url(p.light);
  const dark = url(p.dark);
  const shots = [
    ...(light ? [{ src: { light, dark: light }, alt: `${p.words}, the light picture` }] : []),
    ...(dark ? [{ src: { light: dark, dark }, alt: `${p.words}, the dark picture` }] : []),
  ];
  return (
    <QueueRecord pictures={shots} name={itemsWords(p)} label={p.look === 'label' ? 'Stands for' : 'Shows'}>
      <p>
        {p.words}
        {p.fromLibrary ? '. From Flowology’s picture library.' : '.'}
        {p.request ? ` Asked for: “${p.request}”.` : ''}
      </p>
      {asking ? (
        <>
          <TextField
            label="What should the new one show?"
            description="Another is made in its place, and counts against this month’s picture budget."
            multiline
            value={request}
            onChange={setRequest}
            placeholder="Leave it empty for the house style"
          />
          <span>
            <Button
              isBusy={busy}
              onPress={() => {
                setBusy(true);
                void onTurnDown(request).finally(() => setBusy(false));
              }}
            >
              Turn down and make another
            </Button>{' '}
            <Link onPress={() => setAsking(false)}>Cancel</Link>
          </span>
        </>
      ) : (
        <span>
          <Link onPress={() => setAsking(true)}>Turn down</Link>
        </span>
      )}
    </QueueRecord>
  );
}

/** New category: its name; items go in from their own sheet (pricebook.createCategory). */
function NewCategory({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  return (
    <FormSheet
      isOpen
      onClose={onClose}
      title="New category"
      line="You can then put items in it from their own sheet."
      submitLabel="Add the category"
      isDirty={!!name.trim()}
      isSubmitDisabled={!name.trim()}
      onSubmit={async () => {
        try {
          await client.call('pricebook.createCategory', { name: name.trim() });
        } catch (e) {
          throw new Error(refusal(e), { cause: e });
        }
        onSaved();
      }}
    >
      <TextField label="Name" value={name} onChange={setName} placeholder="like Water heaters" />
    </FormSheet>
  );
}
